import { useEffect, useMemo, useRef, useState } from 'react'
import type { ResearchStreamEvent } from '@/types'

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

interface UseResearchStreamOptions {
  taskId: string | null
  enabled: boolean
  onEvent?: (event: ResearchStreamEvent) => void
  onError?: (error: Error) => void
}

interface UseResearchStreamResult {
  isStreaming: boolean
  elapsedSeconds: number
  stop: () => void
}

function parseSSEBlock(block: string): ResearchStreamEvent | null {
  const lines = block.split('\n')
  let id: number | undefined
  let event = 'message'
  const dataLines: string[] = []

  for (const line of lines) {
    if (line.startsWith('id:')) {
      const parsed = Number(line.slice(3).trim())
      if (!Number.isNaN(parsed)) {
        id = parsed
      }
      continue
    }
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
      continue
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (dataLines.length === 0) {
    return null
  }

  let data: Record<string, unknown> = {}
  const rawData = dataLines.join('\n')
  try {
    data = JSON.parse(rawData) as Record<string, unknown>
  } catch {
    data = { message: rawData }
  }

  return {
    id,
    event,
    data,
    received_at: new Date().toISOString(),
  }
}

export function useResearchStream(options: UseResearchStreamOptions): UseResearchStreamResult {
  const { taskId, enabled, onEvent, onError } = options
  const [isStreaming, setIsStreaming] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const startedAtRef = useRef<number | null>(null)
  const onEventRef = useRef<UseResearchStreamOptions['onEvent']>(onEvent)
  const onErrorRef = useRef<UseResearchStreamOptions['onError']>(onError)

  useEffect(() => {
    onEventRef.current = onEvent
  }, [onEvent])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const stop = useMemo(
    () => () => {
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      setIsStreaming(false)
    },
    []
  )

  useEffect(() => {
    if (!enabled || !taskId) {
      return
    }

    const token = localStorage.getItem('access_token')
    const controller = new AbortController()
    abortRef.current = controller
    setIsStreaming(true)
    setElapsedSeconds(0)
    startedAtRef.current = Date.now()

    const elapsedTimer = window.setInterval(() => {
      if (!startedAtRef.current) return
      const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000)
      setElapsedSeconds(seconds)
    }, 1000)

    let buffer = ''
    const decoder = new TextDecoder()

    const run = async () => {
      try {
        const response = await fetch(`${BASE_URL}/creative-lab/research/${taskId}/stream`, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        })

        if (!response.ok || !response.body) {
          throw new Error(`Stream request failed (${response.status})`)
        }

        const reader = response.body.getReader()
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          let splitIndex = buffer.indexOf('\n\n')
          while (splitIndex !== -1) {
            const block = buffer.slice(0, splitIndex)
            buffer = buffer.slice(splitIndex + 2)

            const parsed = parseSSEBlock(block)
            if (parsed && onEventRef.current) {
              onEventRef.current(parsed)
            }

            splitIndex = buffer.indexOf('\n\n')
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return
        }
        if (onErrorRef.current) {
          onErrorRef.current(error as Error)
        }
      } finally {
        window.clearInterval(elapsedTimer)
        setIsStreaming(false)
      }
    }

    void run()

    return () => {
      window.clearInterval(elapsedTimer)
      controller.abort()
      if (abortRef.current === controller) {
        abortRef.current = null
      }
      setIsStreaming(false)
    }
  }, [enabled, taskId])

  return { isStreaming, elapsedSeconds, stop }
}
